import { clearLoginEmailDraft } from "@/lib/authFormAutofill";

const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";
const JUST_SIGNED_OUT_KEY = "just_signed_out";
const AUTH_ROUTE_PATHS = new Set([
  "/auth",
  "/sign-in",
  "/sign-up",
  "/reset-password",
  "/update-password",
]);

function getPathname(path: string): string {
  return path.split("?")[0].split("#")[0];
}

/**
 * Is Safe App Path.
 */
export function isSafeAppPath(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed.startsWith("/")) return false;
  if (trimmed.startsWith("//")) return false;
  if (trimmed.includes("://")) return false;
  if (trimmed.includes("\\")) return false;
  try {
    const resolved = new URL(trimmed, "https://tellerwand.com");
    if (resolved.origin !== "https://tellerwand.com") return false;
    if (!resolved.pathname.startsWith("/")) return false;
  } catch {
    return false;
  }
  return true;
}

export function buildReturnPath(pathname: string, search = "", hash = ""): string {
  return `${pathname}${search}${hash}`;
}

export function canStorePostLoginRedirect(path: string): boolean {
  if (!isSafeAppPath(path)) return false;
  const pathname = getPathname(path);
  if (AUTH_ROUTE_PATHS.has(pathname)) return false;
  if (pathname === "/") return false;
  return true;
}

export function setPostLoginRedirect(path: string) {
  if (!canStorePostLoginRedirect(path)) return;
  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
}

export function clearPostLoginRedirect() {
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
}

/** Call on sign-out so ProtectedLayout sends users to landing, not login-with-return. */
export function markJustSignedOut() {
  clearPostLoginRedirect();
  clearLoginEmailDraft();
  sessionStorage.setItem(JUST_SIGNED_OUT_KEY, "true");
}

export function consumeJustSignedOut(): boolean {
  const value = sessionStorage.getItem(JUST_SIGNED_OUT_KEY) === "true";
  if (value) {
    sessionStorage.removeItem(JUST_SIGNED_OUT_KEY);
  }
  return value;
}

/** Read stored redirect without consuming (for post-login routing). */
export function readPostLoginRedirect(): string | null {
  const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  if (isSafeAppPath(stored) && canStorePostLoginRedirect(stored)) {
    return stored;
  }
  return null;
}

export function consumePostLoginRedirect(fallbackPath: string) {
  const stored = readPostLoginRedirect();
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  if (stored) return stored;
  return fallbackPath;
}

export interface LocationLike {
  pathname: string;
  search?: string;
  hash?: string;
}

/** Persist the current page so post-login navigation can return here. */
export function storePostLoginRedirectFromLocation(location: LocationLike): void {
  setPostLoginRedirect(
    buildReturnPath(location.pathname, location.search ?? "", location.hash ?? "")
  );
}

/**
 * Read `redirect` from an auth URL (?redirect=/path) and store it when safe.
 * Later values win over an earlier stored path (e.g. comment login then auth link).
 */
export function storeRedirectFromQueryParam(redirect: string | null | undefined): void {
  if (!redirect) return;
  let decoded: string;
  try {
    decoded = decodeURIComponent(redirect);
  } catch {
    decoded = redirect;
  }
  setPostLoginRedirect(decoded);
}
